import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../../config';
import { useOrganization } from '../../context/OrganizationContext';
import { usePlan } from '../../context/PlanContext';

import {
  Buildings,
  CaretRight,
  CheckCircle,
  DotsThreeVertical,
  Eye,
  MagnifyingGlass,
  Plus,
  Robot,
  Rocket,
  Trash,
  TrendUp,
  UsersThree,
  Warning,
  X
} from '@phosphor-icons/react';
/**
 * Client Organizations Management Page
 * 
 * Partner Pro users can:
 * - View all client organizations
 * - Create new client orgs
 * - View aggregate metrics
 * - Deploy templates to clients
 * - Trash client orgs
 */
const ClientOrganizationsPage = () => {
  const navigate = useNavigate();
  const { activeOrganization, isPartnerPro, getActiveOrgId } = useOrganization();
  const { userPlan } = usePlan();
  
  const [clientOrgs, setClientOrgs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [newOrgName, setNewOrgName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deployAgentName, setDeployAgentName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch client organizations
  const fetchClientOrgs = useCallback(async () => {
    try {
      setLoading(true);
      const orgId = getActiveOrgId();
      
      const response = await fetch(`${BACKEND_URL}/api/orgs/clients`, {
        credentials: 'include',
        headers: orgId ? { 'X-Organization-Id': orgId } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setClientOrgs(data.client_organizations || []);
        setError(null);
      } else if (response.status === 403) {
        setError('You must be an admin of a parent organization to manage client organizations.');
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to load client organizations');
      }
    } catch (err) {
      console.error('Error fetching client orgs:', err);
      setError('Network error loading client organizations');
    } finally {
      setLoading(false);
    }
  }, [getActiveOrgId]);

  // Fetch available templates for deployment
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/templates?status=active&limit=50`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || data || []);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  }, []);

  useEffect(() => {
    fetchClientOrgs();
    fetchTemplates();
  }, [fetchClientOrgs, fetchTemplates]);

  // Create new client organization
  const handleCreateOrg = async (e) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;
    
    setActionLoading(true);
    try {
      const orgId = getActiveOrgId();
      const response = await fetch(`${BACKEND_URL}/api/orgs/clients`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(orgId ? { 'X-Organization-Id': orgId } : {})
        },
        body: JSON.stringify({ name: newOrgName })
      });
      
      if (response.ok) {
        setShowCreateModal(false);
        setNewOrgName('');
        await fetchClientOrgs();
      } else {
        const errData = await response.json();
        alert(errData.detail || 'Failed to create organization');
      }
    } catch (err) {
      console.error('Error creating org:', err);
      alert('Network error creating organization');
    } finally {
      setActionLoading(false);
    }
  };

  // Deploy template to client organization
  const handleDeployTemplate = async (e) => {
    e.preventDefault();
    if (!selectedOrg || !selectedTemplate) return;
    
    setActionLoading(true);
    try {
      const orgId = getActiveOrgId();
      const response = await fetch(`${BACKEND_URL}/api/orgs/clients/${selectedOrg.organization_id}/deploy-template`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(orgId ? { 'X-Organization-Id': orgId } : {})
        },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          agent_name: deployAgentName || selectedTemplate.name
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setShowDeployModal(false);
        setSelectedOrg(null);
        setSelectedTemplate(null);
        setDeployAgentName('');
        await fetchClientOrgs();
        alert(`Successfully deployed "${result.agent_name}" to ${selectedOrg.name}`);
      } else {
        const errData = await response.json();
        alert(errData.detail || 'Failed to deploy template');
      }
    } catch (err) {
      console.error('Error deploying template:', err);
      alert('Network error deploying template');
    } finally {
      setActionLoading(false);
    }
  };

  // Trash client organization
  const handleDeleteOrg = async () => {
    if (!selectedOrg) return;
    
    setActionLoading(true);
    try {
      const orgId = getActiveOrgId();
      const response = await fetch(`${BACKEND_URL}/api/orgs/clients/${selectedOrg.organization_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: orgId ? { 'X-Organization-Id': orgId } : {}
      });
      
      if (response.ok) {
        setShowDeleteConfirm(false);
        setSelectedOrg(null);
        await fetchClientOrgs();
      } else {
        const errData = await response.json();
        alert(errData.detail || 'Failed to delete organization');
      }
    } catch (err) {
      console.error('Error deleting org:', err);
      alert('Network error deleting organization');
    } finally {
      setActionLoading(false);
    }
  };

  // Filter orgs by search
  const filteredOrgs = clientOrgs.filter(org => 
    org.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Aggregate metrics
  const totalAgents = clientOrgs.reduce((sum, org) => sum + (org.agent_count || 0), 0);
  const totalActiveAgents = clientOrgs.reduce((sum, org) => sum + (org.active_agent_count || 0), 0);
  const totalMembers = clientOrgs.reduce((sum, org) => sum + (org.member_count || 0), 0);

  // Check access
  if (!isPartnerPro) {
    return (
      <div className="min-h-screen bg-[#0f1015] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <Buildings size={32} className="text-amber-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Partner Pro Required</h2>
          <p className="text-slate-400 mb-6">
            Client organization management is available exclusively on the Partner Pro plan.
          </p>
          <button
            onClick={() => navigate('/settings/billing')}
            className="px-6 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors"
          >
            Upgrade to Partner Pro
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1015] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Buildings className="text-indigo-400" />
              Client Organizations
            </h1>
            <p className="text-slate-400 mt-1">
              Manage your client organizations, deploy templates, and view aggregate metrics
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors"
            data-testid="create-client-org-btn"
          >
            <Plus size={18} />
            Create Client Org
          </button>
        </div>

        {/* Aggregate Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#1a1b23] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                <Buildings size={20} className="text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{clientOrgs.length}</p>
                <p className="text-sm text-slate-400">Client Orgs</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#1a1b23] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Robot size={20} className="text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{totalAgents}</p>
                <p className="text-sm text-slate-400">Total Agents</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#1a1b23] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <TrendUp size={20} className="text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{totalActiveAgents}</p>
                <p className="text-sm text-slate-400">Active Agents</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#1a1b23] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <UsersThree size={20} className="text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{totalMembers}</p>
                <p className="text-sm text-slate-400">Total Members</p>
              </div>
            </div>
          </div>
        </div>

        {/* MagnifyingGlass */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search client organizations..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-[#1a1b23] border border-white/10 
                text-white placeholder:text-slate-500 focus:border-indigo-500 outline-none"
              data-testid="search-client-orgs"
            />
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-3">
            <Warning size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
          </div>
        ) : filteredOrgs.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4">
              <Buildings size={32} className="text-slate-500" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              {searchQuery ? 'No matching organizations' : 'No client organizations yet'}
            </h3>
            <p className="text-slate-400 mb-6">
              {searchQuery 
                ? 'Try a different search term' 
                : 'Create your first client organization to get started'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors"
              >
                Create Client Organization
              </button>
            )}
          </div>
        ) : (
          /* Client Orgs Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="client-orgs-grid">
            {filteredOrgs.map(org => (
              <motion.div
                key={org.organization_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#1a1b23] border border-white/10 rounded-xl p-5 hover:border-white/20 transition-colors group"
                data-testid={`client-org-${org.organization_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                      <Buildings size={20} className="text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white">{org.name}</h3>
                      <p className="text-xs text-slate-500">
                        Created {new Date(org.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  {/* Actions Menu */}
                  <div className="relative">
                    <button
                      onClick={() => setSelectedOrg(selectedOrg?.organization_id === org.organization_id ? null : org)}
                      className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                    >
                      <DotsThreeVertical size={16} />
                    </button>
                    
                    <AnimatePresence>
                      {selectedOrg?.organization_id === org.organization_id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className="absolute right-0 top-10 w-48 bg-[#252630] border border-white/10 rounded-lg shadow-xl z-10"
                        >
                          <button
                            onClick={() => {
                              setShowDeployModal(true);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/5 flex items-center gap-2"
                          >
                            <Rocket size={14} className="text-indigo-400" />
                            Deploy Template
                          </button>
                          <button
                            onClick={() => {
                              // View summary
                              alert(`Viewing summary for ${org.name}:\n\nAgents: ${org.agent_count}\nActive: ${org.active_agent_count}\nMembers: ${org.member_count}`);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/5 flex items-center gap-2"
                          >
                            <Eye size={14} className="text-slate-400" />
                            View Summary
                          </button>
                          <div className="border-t border-white/10 my-1" />
                          <button
                            onClick={() => {
                              setShowDeleteConfirm(true);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2"
                          >
                            <Trash size={14} />
                            Delete Organization
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Stats Row */}
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5">
                    <Robot size={14} className="text-emerald-400" />
                    <span className="text-slate-300">{org.agent_count || 0} agents</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <UsersThree size={14} className="text-purple-400" />
                    <span className="text-slate-300">{org.member_count || 0} members</span>
                  </div>
                </div>

                {/* Active indicator */}
                {org.active_agent_count > 0 && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-400">
                    <CheckCircle size={12} />
                    <span>{org.active_agent_count} active agent{org.active_agent_count !== 1 ? 's' : ''}</span>
                  </div>
                )}

                {/* Quick Deploy Button */}
                <button
                  onClick={() => {
                    setSelectedOrg(org);
                    setShowDeployModal(true);
                  }}
                  className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg 
                    bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 text-sm font-medium 
                    opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Rocket size={14} />
                  Deploy Template
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Create Organization Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#1a1b23] border border-white/10 rounded-2xl p-6 w-full max-w-md"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Create Client Organization</h2>
                <button onClick={() => setShowCreateModal(false)} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleCreateOrg}>
                <div className="mb-6">
                  <label className="block text-sm text-slate-300 mb-2">Organization Name</label>
                  <input
                    type="text"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-[#0f1015] border border-white/10 
                      text-white placeholder:text-slate-500 focus:border-indigo-500 outline-none"
                    placeholder="e.g., Acme Corp, Client A"
                    required
                    autoFocus
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 px-4 py-2.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading || !newOrgName.trim()}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium disabled:opacity-50"
                  >
                    {actionLoading ? 'Creating...' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Deploy Template Modal */}
      <AnimatePresence>
        {showDeployModal && selectedOrg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowDeployModal(false); setSelectedOrg(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#1a1b23] border border-white/10 rounded-2xl p-6 w-full max-w-lg"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">Deploy Template</h2>
                  <p className="text-sm text-slate-400 mt-1">to {selectedOrg.name}</p>
                </div>
                <button onClick={() => { setShowDeployModal(false); setSelectedOrg(null); }} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleDeployTemplate}>
                <div className="mb-4">
                  <label className="block text-sm text-slate-300 mb-2">Select Template</label>
                  <select
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const template = templates.find(t => t.id === e.target.value);
                      setSelectedTemplate(template);
                      setDeployAgentName(template?.name || '');
                    }}
                    className="w-full px-4 py-3 rounded-lg bg-[#0f1015] border border-white/10 
                      text-white focus:border-indigo-500 outline-none"
                    required
                  >
                    <option value="">Choose a template...</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-6">
                  <label className="block text-sm text-slate-300 mb-2">Agent Name (optional)</label>
                  <input
                    type="text"
                    value={deployAgentName}
                    onChange={(e) => setDeployAgentName(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-[#0f1015] border border-white/10 
                      text-white placeholder:text-slate-500 focus:border-indigo-500 outline-none"
                    placeholder="Custom name for the deployed agent"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setShowDeployModal(false); setSelectedOrg(null); }}
                    className="flex-1 px-4 py-2.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading || !selectedTemplate}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Rocket size={16} />
                    {actionLoading ? 'Deploying...' : 'Deploy'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trash Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && selectedOrg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => { setShowDeleteConfirm(false); setSelectedOrg(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#1a1b23] border border-white/10 rounded-2xl p-6 w-full max-w-md"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Warning size={24} className="text-red-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Delete Organization?</h2>
                  <p className="text-sm text-slate-400">{selectedOrg.name}</p>
                </div>
              </div>

              <p className="text-slate-300 mb-6">
                This will permanently delete the organization and all associated data including:
              </p>
              <ul className="text-sm text-slate-400 mb-6 space-y-1 ml-4">
                <li>• All agents ({selectedOrg.agent_count || 0})</li>
                <li>• All team members ({selectedOrg.member_count || 0})</li>
                <li>• All integrations and tokens</li>
                <li>• All opportunities and analytics</li>
              </ul>

              <div className="flex gap-3">
                <button
                  onClick={() => { setShowDeleteConfirm(false); setSelectedOrg(null); }}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteOrg}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white font-medium disabled:opacity-50"
                >
                  {actionLoading ? 'Deleting...' : 'Delete Organization'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClientOrganizationsPage;
